import { useCallback, useEffect, useMemo, useState } from 'react'

type Props<T> = {
  value: T
  onFocus?: Function
  onBlur?: Function
  onChange?: Function
  allowFocusedUpdates?: boolean
  noInternalValueUpdates?: boolean
}

type Ret<T> = {
  value: T
  onFocus: any
  onBlur: any
  onChange: any
  forceInnerUpdate: (value: T) => void
}

function valuesEquivalent<T>(left: T, right: T): boolean {
  if (left === right) return true
  if (typeof left === 'string' && typeof right === 'string') {
    if (left !== '' && right !== '' && !Number.isNaN(Number(left)) && !Number.isNaN(Number(right))) {
      return Number(left) === Number(right)
    }
  }
  return false
}

/**
 * Adds a mechanism to prevent updates to `onChange' while an input is focused.
 * The last update will be applied when the input is blurred.
 *
 * - noInternalValueUpdates: If true, the internal value will not be updated while focused. Important for <AutoComplete> components.
 */

export function useBlockUpdatesWhileFocused<T>({
  value,
  onBlur,
  onChange,
  onFocus,
  allowFocusedUpdates,
  noInternalValueUpdates,
}: Props<T>): Ret<T> {
  const [focused, setFocused] = useState(false)
  const [pendingBlur, setPendingBlur] = useState(false)
  const [innerValue, setInnerValue] = useState<T>(value)

  useEffect(() => {
    if (!focused && !pendingBlur) {
      setInnerValue(value)
    }
  }, [value, focused, pendingBlur])

  useEffect(() => {
    if (!pendingBlur) return
    if (valuesEquivalent(value, innerValue)) {
      setPendingBlur(false)
      setFocused(false)
    }
  }, [value, innerValue, pendingBlur])

  const realValue = useMemo(() => {
    if (allowFocusedUpdates) return value
    if (focused || pendingBlur) return innerValue
    return value
  }, [value, focused, pendingBlur, allowFocusedUpdates, innerValue])

  const handleChange = useCallback(
    (e) => {
      // Get random change events from Autocomplete component
      if (!e && !focused) return
      // Cheap way of accepting event or value (as underlying final-form does also)
      const nextValue = e?.target ? e.target.value : e
      if (focused && !noInternalValueUpdates) setInnerValue(nextValue)
      if (onChange && (!focused || allowFocusedUpdates)) onChange(e, nextValue)
    },
    [onChange, focused, allowFocusedUpdates, noInternalValueUpdates]
  )

  const handleFocus = useCallback(
    (event) => {
      setPendingBlur(false)
      setInnerValue(value)
      setFocused(true)
      onFocus && onFocus(event)
    },
    [onFocus, value]
  )

  const handleBlur = useCallback(
    (event) => {
      let dispatchValue = innerValue
      if (noInternalValueUpdates) {
        dispatchValue = event.target.value
      }
      if (onChange && !allowFocusedUpdates && !valuesEquivalent(dispatchValue, value)) {
        onChange(event, dispatchValue)
        onBlur && onBlur(event)
        setFocused(false)
        setPendingBlur(true)
        return
      }
      onBlur && onBlur(event)
      setFocused(false)
      setPendingBlur(false)
    },
    [onBlur, allowFocusedUpdates, innerValue, onChange, value, noInternalValueUpdates]
  )

  const forceInnerUpdate = useCallback((value: T) => {
    setInnerValue(value)
  }, [])

  return {
    value: realValue,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    forceInnerUpdate,
  }
}
